/**
 * Where to start, in a batch too big to read.
 *
 * A folder run comes back as a flat list of chips in submission order, which
 * is fine for five documents and useless for a hundred: the two that need a
 * person are somewhere in the middle, indistinguishable from the ninety-eight
 * that verified cleanly. This is the same list ordered worst first, with the
 * server's own reason on each row so the queue explains itself before anybody
 * opens anything.
 *
 * The ordering lives in `triage.ts` and is tested there. This file draws it.
 *
 * It judges nothing. PASS, REVIEW and FAIL are the server's words, and the
 * queue is just the rows that are not PASS; a threshold invented here would
 * be a second opinion competing with the verdict that ships.
 */

import { useState } from "preact/hooks";
import { closureLabel, needsReview, triage, type Triaged } from "../triage";
import type { HeldResponse } from "../types";
import { VERDICT_COLOR } from "../types";

export interface TriageProps {
  batch: HeldResponse[];
  held: HeldResponse | null;
  onOpen: (item: HeldResponse) => void;
}

export function Triage({ batch, held, onOpen }: TriageProps) {
  const rows = triage(batch);
  const queue = needsReview(rows);
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? rows : queue;

  return (
    <div class="triage">
      <p class="triage-summary">
        <strong>{batch.length} documents</strong>
        {queue.length === 0 ? (
          <span> · every one verified, nothing to triage</span>
        ) : (
          <span>
            {" "}
            · <strong>{queue.length}</strong> need a look, worst first
          </span>
        )}
        {rows.length > queue.length && (
          <button
            type="button"
            class="link"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? "show only the queue" : `show all ${rows.length}`}
          </button>
        )}
      </p>

      <ul class="triage-list">
        {shown.map((row) => (
          <Row
            key={row.document}
            row={row}
            current={row.item === held}
            onOpen={() => onOpen(row.item)}
          />
        ))}
      </ul>
    </div>
  );
}

function Row({
  row,
  current,
  onOpen,
}: {
  row: Triaged;
  current: boolean;
  onOpen: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        class={current ? "triage-row triage-row-on" : "triage-row"}
        onClick={onOpen}
      >
        <span class="badge" style={{ background: VERDICT_COLOR[row.status] }}>
          {row.status}
        </span>
        <span class="triage-doc">{row.document}</span>
        <span class="muted small">
          {closureLabel(row)}
          {row.confidence === "low" && " · a low-confidence call"}
        </span>
        {/* One reason, not all of them: the row is a pointer, and the panel
            behind it carries the full list. */}
        {row.reasons[0] && (
          <span class="triage-reason muted small">{row.reasons[0]}</span>
        )}
      </button>
    </li>
  );
}
